import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Alert, Box, Link, Snackbar } from '@mui/material';
import { blue } from '@mui/material/colors';
import {
	$laancStatusAuthorized,
	$laancStatusCanceled,
	$primary,
	$warning
} from 'constants/styles/_colors';
import { snackbarAlertsSelector, resetSnackbarAlerts } from 'reducers/globalSlice';
import { stringifyQueryObj } from 'utils/urlHelpers';
import queryString from 'query-string';
import { logoutErrorFoundSelector, setLogoutErrorFound } from 'reducers/userAuthSlice';
import { useEffect } from 'react';

const styles = {
	snackBar: {
		'& .MuiAlert-filledSuccess': {
			backgroundColor: `${$laancStatusAuthorized} !important`
		},
		'& .MuiAlert-filledError': {
			backgroundColor: `${$laancStatusCanceled} !important`
		},
		'& .MuiAlert-filledInfo': {
			bgcolor: `${blue[500]} !important`
		},
		'& .MuiAlert-filledWarning': {
			backgroundColor: `${$warning} !important`
		}
	},
	link: {
		color: $primary,
		fontWeight: 'bold',
		textDecoration: 'underline',
		marginLeft: '5px'
	},
	alert: {
		display: 'flex',
		alignItems: 'center'
	}
};

const SnackbarAlerts = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const alerts = useSelector(snackbarAlertsSelector);
	const logoutErrorFound = useSelector(logoutErrorFoundSelector);
	const queryParameters = queryString.parse(window.location.search);
	const alertsStatuses = alerts?.map(a => a.status);
	const shouldLogOut = alertsStatuses?.includes(401) || alertsStatuses?.includes(403);

	const handleClose = (e, index, reason) => {
		e?.stopPropagation();
		if (reason === 'clickaway') {
			return; //Ignore when handleClose is triggered again by clicking away from the alert component
		}
		const updatedAlerts = [...alerts];
		updatedAlerts.splice(index, 1);
		dispatch(resetSnackbarAlerts(updatedAlerts));
	};

	useEffect(() => {
		if (shouldLogOut && !logoutErrorFound) {
			dispatch(setLogoutErrorFound(true)); //Prevents any future errors from triggering another logout
			return navigate('/logout');
		}
	}, [shouldLogOut, logoutErrorFound, dispatch, navigate]);

	return alerts.map((a, index) => {
		const linkElement = (
			<Link
				onClick={() => {
					//Pass along any query params that were sent by the mobile app
					navigate({
						pathname: a.link,
						search: stringifyQueryObj(queryParameters)
					});
				}}
				component='button'
				style={styles.link}
			>
				{a.linkMessage}
			</Link>
		);

		const duration = a.duration || 5000;
		const autoHide =
			a.autoHide === undefined ? a.type === 'success' || a.type === 'info' : a.autoHide; //Automatically autHide success and info unless explicitly set to false
		const autoHideDuration = autoHide ? duration : null;

		return (
			<Box key={index} style={{ margin: 10 }}>
				<Snackbar
					anchorOrigin={{
						vertical: 'top',
						horizontal: 'right'
					}}
					style={{ top: `${index === 0 ? 20 : index * 70 + 20}px` }}
					open
					sx={styles.snackBar}
					autoHideDuration={autoHideDuration}
					onClose={(e, reason) => handleClose(e, index, reason)}
				>
					<Alert
						elevation={6}
						variant='filled'
						icon={false}
						severity={a.type}
						onClose={e => handleClose(e, index, 'fromAlert')}
					>
						<Box sx={styles.alert}>
							{a.message} {linkElement}
						</Box>
					</Alert>
				</Snackbar>
			</Box>
		);
	});
};

SnackbarAlerts.propTypes = {
	//Not actually a prop, but an expected piece of state from redux
	alerts: PropTypes.arrayOf({
		type: PropTypes.string.isRequired,
		message: PropTypes.string.isRequired,
		status: PropTypes.string,
		linkMessage: PropTypes.string,
		link: PropTypes.string,
		autoHide: PropTypes.bool,
		duration: PropTypes.number
	})
};

export default SnackbarAlerts;
